import ModalCustom from "components/ModalCustom"
import React, { useEffect, useState } from "react"
import { TwitterPicker } from "react-color"
import {
  FaBuilding,
  FaUsers,
  FaUserTie,
  FaSitemap,
  FaProjectDiagram,
  FaNetworkWired,
  FaHome,
  FaChartBar,
  FaUniversity,
  FaHandshake,
} from "react-icons/fa"

// Material Design
import {
  MdBusiness,
  MdGroups,
  MdAccountTree,
  MdDomain,
  MdWork,
} from "react-icons/md"

// Hero Icons 2
import {
  HiBuildingOffice,
  HiBuildingLibrary,
  HiUsers,
  HiUserGroup,
} from "react-icons/hi2"

// Lucide Icons (modern line style)
import { LuBuilding2, LuNetwork, LuBriefcase } from "react-icons/lu"
import Button from "components/Button/Button"
import { useString } from "helpers/hooks"

interface IconPickerModalProps {
  onClose: () => void
  onSelectedIcon: (icon: any, bgIcon: string) => void
  currentIcon: any
  currentBgIcon: string
  isOpen: boolean
}

export const IconPickerModal = ({
  onClose,
  onSelectedIcon,
  currentIcon,
  currentBgIcon,
  isOpen,
}: IconPickerModalProps) => {
  const ORGANIZATION_ICONS = [
    // Font Awesome
    { name: "Building", component: FaBuilding },
    { name: "Users", component: FaUsers },
    { name: "UserTie", component: FaUserTie },
    { name: "Sitemap", component: FaSitemap },
    { name: "ProjectDiagram", component: FaProjectDiagram },
    { name: "NetworkWired", component: FaNetworkWired },
    { name: "Home", component: FaHome },
    { name: "ChartBar", component: FaChartBar },
    { name: "University", component: FaUniversity },
    { name: "Handshake", component: FaHandshake },
    // Material Design
    { name: "Business", component: MdBusiness },
    { name: "Groups", component: MdGroups },
    { name: "AccountTree", component: MdAccountTree },
    { name: "Domain", component: MdDomain },
    { name: "Work", component: MdWork },
    // HeroIcons
    { name: "BuildingOffice", component: HiBuildingOffice },
    { name: "BuildingLibrary", component: HiBuildingLibrary },
    { name: "Users2", component: HiUsers },
    { name: "UserGroup", component: HiUserGroup },
    // Lucide
    { name: "Building2", component: LuBuilding2 },
    { name: "Network", component: LuNetwork },
    { name: "Briefcase", component: LuBriefcase },
  ]
  const selectedBgIcon = useString("#1DA1F2")
  const [selectedIcon, setSelectedIcon] = useState<any>(null)
  useEffect(() => {
    if (isOpen) {
      setSelectedIcon(currentIcon)
      selectedBgIcon.setValue(currentBgIcon || "#1DA1F2")
    }
  }, [currentIcon, currentBgIcon, isOpen])

  const handleSave = () => {
    onSelectedIcon(selectedIcon, selectedBgIcon.value)
    onClose()
  }
  const renderBody = () => {
    return (
      <div className="flex flex-col mb-[24px]">
        <p className="text-[14px] text-[#222222] leading-[24px]">
          Choose an icon to represent your workspace. You can select from the
          suggestions below.
        </p>
        <div className="flex flex-wrap gap-2 my-[16px]">
          {ORGANIZATION_ICONS.map((iconData, idx) => {
            const isActive = selectedIcon?.name === iconData.name
            return (
              <button
                key={idx}
                className="border rounded-[6px] p-[6px] cursor-pointer"
                style={{
                  borderColor: isActive ? "#FDBE44" : "#E4E4E4",
                  backgroundColor: isActive ? "#FDBE44" : "#FFFFFF",
                }}
                onClick={() => {
                  if (isActive) {
                    setSelectedIcon(null)
                    return
                  }
                  setSelectedIcon(iconData)
                }}
              >
                {React.createElement(
                  iconData.component as React.ComponentType<{ size?: number }>,
                  { size: 20 }
                )}
              </button>
            )
          })}
          <p className="text-[14px] text-[#222222] leading-[24px] mt-[8px]">
            Choose a background color for your workspace.
          </p>
          <TwitterPicker
            color={selectedBgIcon.value}
            onChangeComplete={(c) => selectedBgIcon.setValue(c.hex)}
            triangle="hide"
            style={{
              backgroundColor: "#222222",
            }}
            colors={[
              "#1DA1F2",
              "#E0245E",
              "#17BF63",
              "#794BC4",
              "#FFAD1F",
              "#F45D22",
              "#2F3542",
              "#A4B0BE",
            ]}
          />
        </div>
        <div className="flex flex-col md:flex-row gap-y-3 md:gap-y-0 justify-between">
          <Button
            widthBtn={"100%"}
            title="Cancel"
            colorBtn="white"
            onClick={() => {
              onClose()
            }}
          />
          <Button
            widthBtn="100%"
            title="Save"
            colorBtn="yellow"
            onClick={handleSave}
            isDisabledBtn
            disabled={!selectedIcon?.name}
          />
        </div>
      </div>
    )
  }
  return (
    <ModalCustom
      label="Select Icon"
      widthModal={500}
      styleHeaderModal={{
        height: "auto",
        paddingTop: 12,
        alignItems: "start",
      }}
      bodyChildren={renderBody()}
    />
  )
}
